import React, { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

export enum FeedbackContext {
  OVERALL_APPLICATION = 'overall_application',
  CLIENT_ONBOARDING = 'client_onboarding',
  CLIENT_DETAILS_DIETARY_GOALS = 'client_details_dietary_goals',
  TARGET_NUTRITIONAL_ANALYSIS = 'target_nutritional_analysis',
  AI_MEAL_PLANNING_OVERALL = 'ai_meal_planning_overall',
  AUTOMATED_MEAL_PLANNING_OVERALL = 'automated_meal_planning_overall',
  MANUAL_MEAL_PLANNING_OVERALL = 'manual_meal_planning_overall',
  AI_MEAL_RECIPE_QUALITY = 'ai_meal_recipe_quality',
  AI_MEAL_NUTRITIONAL_ANALYSIS = 'ai_meal_nutritional_analysis',
  AUTO_MEAL_NUTRITION_ANALYSIS = 'auto_meal_nutrition_analysis',
  MANUAL_MEAL_NUTRITION_ANALYSIS = 'manual_meal_nutrition_analysis',
  CUSTOM_RECIPE = 'custom_recipe',
  NUTRITIONIST_PROFILE = 'nutritionist_profile',
  MANUAL_MEAL_PLAN_GENERATION = 'manual_meal_plan_generation',
  AI_MEAL_PLAN_GENERATION = 'ai_meal_plan_generation',
  AUTO_MEAL_PLAN_GENERATION = 'auto_meal_plan_generation',
}

const FEEDBACK_CONTEXT_LABELS: Record<FeedbackContext, string> = {
  [FeedbackContext.OVERALL_APPLICATION]: 'Overall Application',
  [FeedbackContext.CLIENT_ONBOARDING]: 'Client Onboarding',
  [FeedbackContext.CLIENT_DETAILS_DIETARY_GOALS]: 'Client Details - Client Goals, Dietary Preferences and Meal types',
  [FeedbackContext.TARGET_NUTRITIONAL_ANALYSIS]: 'Target Nutritional Analysis',
  [FeedbackContext.AI_MEAL_PLANNING_OVERALL]: 'AI Meal Planning Overall',
  [FeedbackContext.AUTOMATED_MEAL_PLANNING_OVERALL]: 'Automated Meal Planning Overall',
  [FeedbackContext.MANUAL_MEAL_PLANNING_OVERALL]: 'Manual Meal Planning Overall',
  [FeedbackContext.AI_MEAL_RECIPE_QUALITY]: 'AI Meal - Recipe Quality',
  [FeedbackContext.AI_MEAL_NUTRITIONAL_ANALYSIS]: 'AI Meal - Nutritional Analysis',
  [FeedbackContext.AUTO_MEAL_NUTRITION_ANALYSIS]: 'Auto Meal - Nutrition Analysis',
  [FeedbackContext.MANUAL_MEAL_NUTRITION_ANALYSIS]: 'Manual Meal - Nutrition Analysis',
  [FeedbackContext.CUSTOM_RECIPE]: 'Custom Recipe',
  [FeedbackContext.NUTRITIONIST_PROFILE]: 'Nutritionist Profile',
  [FeedbackContext.MANUAL_MEAL_PLAN_GENERATION]: 'Manual Meal Plan Generation',
  [FeedbackContext.AI_MEAL_PLAN_GENERATION]: 'AI Meal Plan Generation',
  [FeedbackContext.AUTO_MEAL_PLAN_GENERATION]: 'Auto Meal Plan Generation',
};

const FEEDBACK_CONTEXT_OPTIONS = Object.values(FeedbackContext).map((value) => ({
  value,
  label: FEEDBACK_CONTEXT_LABELS[value],
}));

const RATING_OPTIONS = [
  { value: "1", label: "1 - Very Poor" },
  { value: "2", label: "2 - Poor" },
  { value: "3", label: "3 - Average" },
  { value: "4", label: "4 - Good" },
  { value: "5", label: "5 - Excellent" },
] as const;

export interface FeedbackPayload {
  result?: "pass" | "fail";
  title?: string;
  details: string;
  context: FeedbackContext;
  rating: string;
}

export type FeedbackPanelVariant = 'default' | 'compact' | 'inline' | 'minimal';

interface FeedbackPanelProps {
  title?: string;
  description?: string;
  titlePlaceholder?: string;
  detailsPlaceholder?: string;
  detailsRows?: number;
  defaultContext?: FeedbackContext;
  defaultRating?: string;
  isSubmitting?: boolean;
  submitLabel?: string;
  onSubmit?: (payload: FeedbackPayload) => Promise<void> | void;
  className?: string;
  variant?: FeedbackPanelVariant;
  hideHeader?: boolean;
  hideDescription?: boolean;
}

const FeedbackPanel: React.FC<FeedbackPanelProps> = ({
  title = "Share Feedback",
  description = "Share your feedback and help us improve the application.",
  titlePlaceholder = "Short summary",
  detailsPlaceholder = "Please provide details about your feedback. Include steps or data to help us understand the issue.",
  detailsRows,
  defaultContext = FeedbackContext.OVERALL_APPLICATION,
  defaultRating,
  isSubmitting = false,
  submitLabel = "Submit Feedback",
  onSubmit,
  className,
  variant = "default",
  hideHeader = false,
  hideDescription = false,
}) => {
  const [context, setContext] = useState<FeedbackContext>(defaultContext);
  const [rating, setRating] = useState<string>(defaultRating || "");
  const [details, setDetails] = useState("");
  const [error, setError] = useState<string | null>(null);

  const isValid = useMemo(
    () => Boolean(details.trim() && rating),
    [details, rating],
  );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isValid || isSubmitting) {
      setError("Please fill in the details and rating before submitting.");
      return;
    }

    const payload: FeedbackPayload = {
      context,
      rating,
      details: details.trim(),
    };

    try {
      setError(null);
      await onSubmit?.(payload);
      setDetails("");
      setContext(defaultContext);
      setRating(defaultRating || "");
    } catch (submitError) {
      console.error("[FeedbackPanel] submit failed", submitError);
      setError("Something went wrong. Please try again.");
    }
  };

  const spacingClass = variant === "compact" || variant === "minimal" ? "space-y-3" : variant === "inline" ? "space-y-4" : "space-y-5";
  const cardPadding = variant === "compact" ? "p-3" : variant === "minimal" ? "p-2" : "p-6";
  const inputSize = variant === "compact" || variant === "minimal" ? "text-sm h-9" : "h-10";
  const textSize = variant === "compact" || variant === "minimal" ? "text-xs" : "text-sm";
  const titleSize = variant === "compact" || variant === "minimal" ? "text-base" : "text-lg";
  const isInline = variant === "inline";
  const isMinimal = variant === "minimal";
  const textareaRows = detailsRows ?? (variant === "compact" || variant === "minimal" ? 3 : 4);

  const cardContent = (
    <form className={spacingClass} onSubmit={handleSubmit}>
      {isInline ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            <div className="space-y-2">
              <Label htmlFor="feedback-context" className={textSize}>Context</Label>
              <Select
                value={context}
                onValueChange={(value) =>
                  setContext(value as FeedbackContext)
                }
              >
                <SelectTrigger id="feedback-context" className={inputSize}>
                  <SelectValue placeholder="Select a context" />
                </SelectTrigger>
                <SelectContent>
                  {FEEDBACK_CONTEXT_OPTIONS.map(({ value, label }) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="feedback-rating" className={textSize}>Rating <span className="text-destructive">*</span></Label>
              <Select value={rating} onValueChange={setRating} required>
                <SelectTrigger id="feedback-rating" className={inputSize}>
                  <SelectValue placeholder="Select rating" />
                </SelectTrigger>
                <SelectContent>
                  {RATING_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className={cn("space-y-2", variant === "minimal" && "space-y-1")}>
            <Label htmlFor="feedback-context" className={textSize}>Context</Label>
            <Select
              value={context}
              onValueChange={(value) =>
                setContext(value as FeedbackContext)
              }
            >
              <SelectTrigger id="feedback-context" className={inputSize}>
                <SelectValue placeholder="Select a context" />
              </SelectTrigger>
              <SelectContent>
                {FEEDBACK_CONTEXT_OPTIONS.map(({ value, label }) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className={cn("space-y-2", variant === "minimal" && "space-y-1")}>
            <Label htmlFor="feedback-rating" className={textSize}>Rating <span className="text-destructive">*</span></Label>
            <Select value={rating} onValueChange={setRating} required>
              <SelectTrigger id="feedback-rating" className={inputSize}>
                <SelectValue placeholder="Select rating" />
              </SelectTrigger>
              <SelectContent>
                {RATING_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </>
      )}

      <div className={cn("space-y-2", variant === "minimal" && "space-y-1")}>
        <Label htmlFor="feedback-details" className={textSize}>Details <span className="text-destructive">*</span></Label>
        <Textarea
          id="feedback-details"
          placeholder={detailsPlaceholder}
          value={details}
          onChange={(event) => setDetails(event.target.value)}
          rows={textareaRows}
          required
          className={cn(variant === "minimal" && "text-sm")}
        />
      </div>

      {error && <p className={cn("text-destructive", textSize)}>{error}</p>}

      <div className={cn(
        "flex",
        isInline ? "justify-between items-center" : "justify-end",
        isMinimal && "pt-1"
      )}>
        {isInline && !isMinimal && (
          <div className="flex-1" />
        )}
        <Button 
          type="submit" 
          disabled={!isValid || isSubmitting}
          size={variant === "compact" || isMinimal ? "sm" : "default"}
          className={cn(isMinimal && "h-8 text-xs")}
        >
          {isSubmitting ? "Submitting..." : submitLabel}
        </Button>
      </div>
    </form>
  );

  if (isMinimal && hideHeader) {
    return (
      <div className={cn("border rounded-lg", className)}>
        <div className={cardPadding}>
          {cardContent}
        </div>
      </div>
    );
  }

  return (
    <Card className={cn(className, variant === "minimal" && "border-0 shadow-none")}>
      {!hideHeader && (
        <CardHeader className={cn(variant === "compact" && "pb-3", variant === "minimal" && "pb-2 px-2 pt-2")}>
          <CardTitle className={cn("font-semibold", titleSize)}>{title}</CardTitle>
          {description && !hideDescription && (
            <p className={cn("text-muted-foreground", variant === "minimal" ? "text-xs" : textSize)}>{description}</p>
          )}
        </CardHeader>
      )}
      <CardContent className={cn(variant === "minimal" && cardPadding, variant !== "minimal" && variant !== "compact" && "pt-6")}>
        {cardContent}
      </CardContent>
    </Card>
  );
};

export default FeedbackPanel;

